import type { CopyActionConfig, FlowNode } from '@lighthouse/shared'
import { getAllCanBeUpstreamNodes, getUpstreamNodesOptions, useAtomData } from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import React, { useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import * as CM from '../styles'
import { extractDataFn, textRichVariableName } from '../util'
import * as SC from './styles'

type CopyCardProps = {
    config: CopyActionConfig
}

const CopyCard: React.FC<CopyCardProps> = ({ config }) => {
    const { copyText } = config
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)

    const userOption = useGetUserRecordOptions()

    const extractData = useMemo(() => extractDataFn(copyText), [copyText])

    const nodes = useNodesData(extractData.nodeIds) as FlowNode[]

    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodes || []),
                      allUpstreamNodes: nodes
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodes]
    )

    if (!extractData.objects.length) {
        return null
    }

    return (
        <CM.CardItemContainer>
            <CM.FieldItemContent>
                <CM.FieldItemTitle whiteSpace="pre-wrap">
                    {extractData.objects.map(item => {
                        if (item.type === 'text') {
                            return <SC.Text key={item.value}>{item.value}</SC.Text>
                        }
                        const text = textRichVariableName({
                            userOption,
                            upstreamOptions,
                            data: item
                        })
                        return (
                            <SC.Text key={text} color="var(--color-purple-500)">
                                {text}
                            </SC.Text>
                        )
                    })}
                </CM.FieldItemTitle>
            </CM.FieldItemContent>
        </CM.CardItemContainer>
    )
}

export default CopyCard
